import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import logoColor from '@/assets/brand/signcare-logo.svg';
import mobileFrame from '@/assets/images/mobile-frame.png';
import { cn } from '@/lib/utils';

import type { BuilderSection } from '../builderModel';
import {
  type QuestionnaireAnswers,
  type RenderableSection,
  totalUploadBytes,
} from '../elements/answers';
import { QuestionnaireRenderer } from '../elements/QuestionnaireRenderer';

// Phone geometry from the designer's frame image — the same constants the workflow builder's
// MobilePreview uses, so the two previews are the same size inside the product.
const PHONE_W = 388;
const PHONE_H = 802;

/**
 * The caption and pagination row UNDER the phone: `mt-4` + one 1.5em line, then `mt-3 mb-2` around a
 * 28px control row.
 *
 * THIS CONSTANT IS WHY THE CONTINUE BUTTON WAS BEING CUT OFF. The bounding box was sized to
 * `PHONE_H * scale`, but the block inside it is the phone PLUS this chrome — about 88px more — and
 * the column is `overflow-hidden`, so the surplus was clipped from the bottom. It only became
 * obvious when the preview column got narrower: a smaller `scale` moved the clip line up from the
 * empty bezel onto the button itself.
 *
 * Both the measured fit and the box now use the full height, so nothing can be cut.
 */
const CAPTION_H = 88;
const CONTENT_H = PHONE_H + CAPTION_H;

/**
 * The designer scales their preview to 85% inside the column (`scale * 0.85`) rather than filling it,
 * so the phone never touches the panel edges.
 */
const FIT = 0.85;

/** Breathing room inside the column before the phone starts scaling down. */
const GUTTER = 24;

/**
 * The builder's live phone preview.
 *
 * THIS REPLACED A HARDCODED MOCK. The previous version drew a fixed
 * Name / Email / Phone Number / Description form and five pagination dots, ignoring the model
 * entirely — so it showed the same four fields whatever you built, and an admin could add ten
 * questions and see none of them. It was a picture of a questionnaire, not a preview of theirs.
 *
 * It renders through `QuestionnaireRenderer`, the same component the applicant's screen uses, so
 * what an admin sees here is the form itself rather than a second implementation of it. Answers
 * are held in local state and thrown away: the point is that the controls behave — a dropdown
 * cascade actually cascades, a consent box actually ticks — because a preview you cannot operate
 * cannot tell you the form works.
 */
export function QuestionnairesMobilePreview({
  sections,
  activeSectionKey,
  onSelectSection,
}: {
  sections: BuilderSection[];
  activeSectionKey: string;
  onSelectSection: (key: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  /**
   * Preview answers, per section. Local and discarded — nothing here is ever submitted.
   *
   * Keyed by section so paging away and back does not wipe what was typed, which would make the
   * preview feel broken while an admin compared two sections.
   */
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      // Fitted against CONTENT_H, not PHONE_H — the caption and dots have to fit too, or they are
      // clipped along with the bottom of the phone.
      const s = Math.min((rect.width - GUTTER) / PHONE_W, (rect.height - GUTTER) / CONTENT_H, 1);
      setScale(Math.max(s, 0.1));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activeIndex = Math.max(
    sections.findIndex((s) => s.key === activeSectionKey),
    0,
  );
  const activeSection = sections[activeIndex] ?? sections[0];

  // The section as the renderer wants it. `title` falls back to the builder tab name so an
  // admin who has not written a heading yet still sees "Section 1" rather than an empty band.
  const renderable = useMemo<RenderableSection>(
    () => ({
      // The builder's client-side key. A section may not be saved yet, so there is no server id
      // to use — and the preview's answers are thrown away, so it never needs one.
      id: activeSection?.key ?? '',
      title: activeSection?.title.trim() || activeSection?.name || 'Section',
      description: activeSection?.description ?? '',
      fields: (activeSection?.fields ?? []).map((f) => ({
        id: f.key,
        fieldType: f.fieldType,
        label: f.label,
        placeholder: f.placeholder,
        validationMessage: f.validationMessage,
        isMandatory: f.isMandatory,
        config: f.config,
      })),
    }),
    [activeSection],
  );

  const sectionKey = activeSection?.key ?? '';
  const sectionAnswers = answers[sectionKey] ?? {};

  function handleChange(fieldId: string, value: unknown) {
    setAnswers((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] ?? {}), [fieldId]: value },
    }));
  }

  function goTo(index: number) {
    const target = sections[Math.min(Math.max(index, 0), sections.length - 1)];
    if (target) onSelectSection(target.key);
  }

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden border-s border-[#E5E7EB] bg-[#F4F6FD]"
    >
      {/* The box reserves the SCALED size of everything inside — phone + caption + dots — so the
          column has room for the whole block. Sized to the phone alone, the rest overflowed into the
          panel's `overflow-hidden` and was clipped. */}
      <div
        className="relative shrink-0"
        style={{ width: PHONE_W * scale * FIT, height: CONTENT_H * scale * FIT }}
      >
        <div
          className="absolute top-0 left-0 flex flex-col items-center"
          style={{
            width: PHONE_W,
            height: CONTENT_H,
            transform: `scale(${scale * FIT})`,
            transformOrigin: 'top left',
          }}
        >
          {/* The designer's frame image at their exact geometry — bezel, island, clock and status
              icons are drawn into the PNG, which is why the top inset is 70px and there is no
              hand-drawn status bar. Same treatment as the workflow builder's preview. */}
          <div
            className="relative flex shrink-0 flex-col select-none drop-shadow-xl"
            style={{
              width: PHONE_W,
              height: PHONE_H,
              paddingTop: 70,
              paddingBottom: 24,
              paddingLeft: 24,
              paddingRight: 24,
              backgroundImage: `url(${mobileFrame})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          >
            {/* One rounded clipping container around content AND footer — with the radius on the
                content alone the footer's white background keeps square corners and spills over
                the frame's rounded bottom. */}
            <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-[32px] bg-white">
              <div className="hide-scrollbar flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto px-1 pt-1 pb-2 text-center">
                <QuestionnaireRenderer
                  section={renderable}
                  answers={sectionAnswers}
                  // Across every section, so the preview shows the same remaining upload budget an
                  // applicant would see — the point of sharing one renderer.
                  stepUploadBytes={totalUploadBytes(answers)}
                  onChange={handleChange}
                  // The logo the designer's preview shows above the section heading.
                  //
                  // A DELIBERATE DIVERGENCE FROM THE LIVE RUN, decided 2026-08-19 — do not "fix" it.
                  // The real applicant session shows the brand mark on the WELCOME screen only; each
                  // step after it carries its own title (see SessionMobilePanel). This builder preview
                  // keeps the logo because the designer's mock has it and it frames the section
                  // heading, while `QuestionnaireStepPreview` — the workflow builder's preview of the
                  // journey — correctly omits it, because that one exists to mirror the real screens.
                  //
                  // The bundled SignCare mark, never the tenant's: the logo here is chrome, and
                  // reaching into Customization for it would also make this feature depend on another.
                  logoUrl={logoColor}
                  emptyHint="No fields added yet in this section"
                />
              </div>

              {/* Structurally IDENTICAL to the workflow builder's preview footer and to the real
                  applicant screen (`SessionMobilePanel`): CTA, then the "Secured by" line, pinned with
                  `mt-auto pt-4`. Three places, one shape.

                  The "Secured by" line is not decoration here — it is what holds the button clear of
                  the bottom. I had removed it to match the designer's static mock, which left the
                  button flush against a `rounded-[32px] overflow-hidden` container: its corners were
                  clipped by the radius and it ran into the frame's bezel. That is the break. */}
              <div className="mt-auto flex flex-col gap-2.5 bg-white px-1 pt-4">
                {/* Never disabled, and it WRAPS to the first section on the last one — the designer's
                    behaviour. Ours was disabled whenever there was one section, which is the default
                    state of a new questionnaire, so the primary button on the preview was greyed out
                    the entire time anyone was building. */}
                <button
                  type="button"
                  onClick={() => goTo(activeIndex >= sections.length - 1 ? 0 : activeIndex + 1)}
                  className="w-full border py-3 text-[length:var(--text-16)] font-medium"
                  style={{
                    backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
                    borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
                    color: 'var(--brand-btn-text, #fff)',
                    borderRadius: 'var(--brand-btn-radius, 8px)',
                  }}
                >
                  Continue
                </button>

                {/* On EVERY screen, exactly as the real run does — always the SignCare mark, never the
                    tenant's, because the line states who secures the verification. Substituting the
                    customer's own logo would make it say something untrue. */}
                <div className="flex items-center justify-center gap-1.5 text-[length:var(--text-10)] text-primary">
                  <span>Secured by</span>
                  <img
                    src={logoColor}
                    alt="SignCare"
                    className="h-3 w-auto max-w-[84px] shrink-0 object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Which section this is, captioned under the phone. Rendered always with one line
              reserved: the column is `justify-center`, so a block that appears and disappears
              shifts the whole phone vertically. */}
          <p className="mt-4 min-h-[1.5em] text-center text-[length:var(--text-14)] font-semibold leading-[1.5] text-[#041A3A]">
            {activeSection?.title.trim() || activeSection?.name}
          </p>

          <div className="mt-3 mb-2 flex shrink-0 items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => goTo(activeIndex - 1)}
              disabled={activeIndex === 0}
              aria-label="Previous section"
              className="mx-1 flex h-7 w-7 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white text-black shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            {sections.map((section, index) => (
              <button
                key={section.key}
                type="button"
                onClick={() => onSelectSection(section.key)}
                aria-label={`Go to ${section.name}`}
                className={cn(
                  'rounded-full transition-all',
                  activeIndex === index ? 'h-2 w-4 bg-[#111827]' : 'h-2 w-2 bg-[#D1D5DB]',
                )}
              />
            ))}
            <button
              type="button"
              onClick={() => goTo(activeIndex + 1)}
              disabled={activeIndex >= sections.length - 1}
              aria-label="Next section"
              className="mx-1 flex h-7 w-7 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white text-black shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
